// client/src/lib/tracking.js
// Meta Pixel + LinkedIn Insight Tag + puente browser -> Conversions API.
//
// Cada conversión se envía DOS veces a propósito: una desde el navegador (pixel)
// y otra desde nuestro servidor (CAPI), compartiendo el mismo eventID para que
// Meta las deduplique. Si un adblocker o iOS bloquea el pixel, la copia
// server-side igual llega, que es justamente el punto de tener CAPI.
//
// Sin REACT_APP_META_PIXEL_ID configurado, todo esto es un no-op silencioso.

import { isClientArea } from './client-area';

const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const LI_PARTNER_ID = process.env.REACT_APP_LINKEDIN_PARTNER_ID;

const CAPI_ENDPOINT = '/api/meta/track';

/* ─── helpers ─── */

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getCookie(name) {
  try {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  } catch (e) {
    return null;
  }
}

function getParam(name) {
  try {
    return new URLSearchParams(window.location.search).get(name) || null;
  } catch (e) {
    return null;
  }
}

/** _fbp lo escribe el propio pixel. Puede no existir si el pixel está bloqueado. */
function getFbp() {
  return getCookie('_fbp');
}

/**
 * _fbc lo escribe el pixel a partir de ?fbclid. Si el pixel aún no corrió (o está
 * bloqueado) pero la URL trae fbclid, lo construimos con el formato que Meta espera:
 * fb.<subdomainIndex>.<creationTime>.<fbclid>
 */
function getFbc() {
  var existing = getCookie('_fbc');
  if (existing) return existing;
  var fbclid = getParam('fbclid');
  if (!fbclid) return null;
  return 'fb.1.' + Date.now() + '.' + fbclid;
}

/* ─── inyección de scripts ─── */

function injectMetaPixel(pixelId) {
  if (!pixelId || window._monacoMetaPixelLoaded) return;
  window._monacoMetaPixelLoaded = true;
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){
    n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window,document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  // autoConfig off: no queremos que Meta infiera eventos desde clicks en botones,
  // porque duplicaría los eventos que disparamos explícitamente acá.
  window.fbq('set', 'autoConfig', false, pixelId);
  window.fbq('init', pixelId);
}

function injectLinkedInPixel(partnerId) {
  if (!partnerId || window._monacoLiPixelLoaded) return;
  window._monacoLiPixelLoaded = true;
  window._linkedin_partner_id = partnerId;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(partnerId);
  (function (l) {
    if (!l) {
      window.lintrk = function (a, b) {
        window.lintrk.q.push([a, b]);
      };
      window.lintrk.q = [];
    }
    var s = document.getElementsByTagName('script')[0];
    var b = document.createElement('script');
    b.type = 'text/javascript';
    b.async = true;
    b.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
    s.parentNode.insertBefore(b, s);
  })(window.lintrk);
}

/* ─── envío server-side (CAPI) ─── */

function sendToCapi(eventName, eventId, customData, extra) {
  if (!META_PIXEL_ID || isClientArea()) return;

  var body = {
    eventName: eventName,
    eventId: eventId,
    eventSourceUrl: window.location.href,
    fbp: getFbp(),
    fbc: getFbc(),
    externalId: (function () {
      try {
        return localStorage.getItem('monaco_anon_id');
      } catch (e) {
        return null;
      }
    })(),
    customData: customData || {},
  };

  // URI del invitee de Calendly: el servidor lo usa para traer email y nombre
  // desde la API de Calendly y mandarlos hasheados, lo que sube muchísimo el
  // match quality del evento en Meta.
  if (extra && extra.calendlyInviteeUri) {
    body.calendlyInviteeUri = extra.calendlyInviteeUri;
  }

  try {
    // keepalive para que el request sobreviva si el usuario navega justo después.
    fetch(CAPI_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(function () {
      /* el tracking nunca debe romper la página */
    });
  } catch (e) {
    /* swallow */
  }
}

/* ─── API pública ─── */

/**
 * Inyecta los pixeles y dispara el PageView. Llamar una vez por carga de página.
 * Idempotente: se puede llamar desde varias páginas sin duplicar el script.
 */
export function initPixels() {
  if (isClientArea()) return;
  injectMetaPixel(META_PIXEL_ID);
  injectLinkedInPixel(LI_PARTNER_ID);

  if (META_PIXEL_ID && !window._monacoPageViewSent) {
    window._monacoPageViewSent = true;
    track('PageView');
  }

  if (LI_PARTNER_ID) {
    var liPageviewConvId = Number(process.env.REACT_APP_LI_PAGEVIEW_CONVERSION_ID);
    if (liPageviewConvId && window.lintrk) {
      window.lintrk('track', { conversion_id: liPageviewConvId });
    }
  }
}

/**
 * Dispara un evento estándar de Meta por pixel y por CAPI con el mismo eventID.
 *
 * @param {string} eventName   PageView | ViewContent | Lead | Schedule | Contact
 * @param {object} [customData] content_name, value, currency, etc.
 * @param {object} [extra]     { eventId, calendlyInviteeUri, liConversionId }
 *
 * extra.eventId permite forzar un id determinista. Lo usa la reserva de
 * Calendly: el webhook del servidor manda el mismo evento con el mismo id
 * derivado del UUID del invitee, así Meta deduplica en vez de contar dos
 * conversiones por la misma reunión.
 */
export function track(eventName, customData, extra) {
  if (isClientArea()) return;
  var eventId = (extra && extra.eventId) || uuid();

  if (META_PIXEL_ID && window.fbq) {
    window.fbq('track', eventName, customData || {}, { eventID: eventId });
  }

  sendToCapi(eventName, eventId, customData, extra);

  if (extra && extra.liConversionId && window.lintrk) {
    window.lintrk('track', { conversion_id: Number(extra.liConversionId) });
  }
}

/** true si hay un pixel configurado. Útil para no mostrar UI de debug en prod. */
export function isPixelConfigured() {
  return !!META_PIXEL_ID;
}
